import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Skeleton, Stack, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import { useBranchReceipts } from '../../hooks/useBranchReceipts';
import { IBranchReceiptsCurrencyTotal } from '../../types';
import { CASH_DEPOSIT_LIMIT, CASH_DEPOSIT_WARNING } from '../../constants';

export function formatCashAmount(value: number, currency: string): string {
	return (
		`${currency} ` +
		new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value)
	);
}

export const cashBoxInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué hay acá? 👀
		</Typography>
		<Typography variant="body2" gutterBottom>
			El efectivo que se cobró en la sucursal 💵 y todavía no se depositó en el banco.
		</Typography>
		<Typography variant="body2" gutterBottom>
			Ves cuántos recibos hay acumulados, el total en moneda principal y cómo se compone por moneda.
		</Typography>
		<Typography variant="body2">
			Regla de oro 🏦: la caja no debe superar los USD 5.000. Cuando se acerque, andá planificando la ida al banco.
		</Typography>
	</>
);

interface CashTotalsOverviewProps {
	totalReceipts: number;
	totalAmount: number;
	byCurrency: IBranchReceiptsCurrencyTotal[];
}

/** Cuerpo compartido: totales, barra de progreso y alertas de límite. */
export function CashTotalsOverview({ totalReceipts, totalAmount, byCurrency }: CashTotalsOverviewProps): JSX.Element {
	const overLimit = totalAmount >= CASH_DEPOSIT_LIMIT;
	const nearLimit = !overLimit && totalAmount >= CASH_DEPOSIT_WARNING;

	if (totalReceipts === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No hay recibos por depositar 🙌
			</Typography>
		);
	}

	return (
		<>
			<Box display="flex" alignItems="baseline" gap={2} flexWrap="wrap" mb={1}>
				<Typography variant="body1">
					Tenemos <strong>{totalReceipts.toLocaleString('es-AR')}</strong> recibo
					{totalReceipts !== 1 ? 's' : ''} sin depositar
				</Typography>
				<Typography variant="h6" fontWeight={700} color={overLimit ? 'error.main' : 'text.primary'}>
					{formatCashAmount(totalAmount, 'USD')}
				</Typography>
			</Box>

			{byCurrency.length > 0 && (
				<Stack direction="row" flexWrap="wrap" sx={{ gap: 1, mb: 1.5 }}>
					{byCurrency.map((c) => (
						<Chip
							key={c.transactionCurrency}
							label={formatCashAmount(Number(c.totalTransactionAmount), c.transactionCurrency)}
							size="small"
							variant="outlined"
						/>
					))}
				</Stack>
			)}

			<LinearProgress
				variant="determinate"
				value={Math.min((totalAmount / CASH_DEPOSIT_LIMIT) * 100, 100)}
				color={overLimit ? 'error' : nearLimit ? 'warning' : 'success'}
				sx={{ height: 8, borderRadius: 4, mb: overLimit || nearLimit ? 1.5 : 0 }}
			/>

			{overLimit && (
				<Alert severity="error" icon={<AccountBalanceIcon fontSize="inherit" />}>
					La caja llegó al límite de {formatCashAmount(CASH_DEPOSIT_LIMIT, 'USD')} — es hora de depositar estos recibos
					en el banco 🏦
				</Alert>
			)}
			{nearLimit && (
				<Alert severity="warning">
					Ojo 👀: la caja se está acercando al límite. Recordá que el monto no debe superar los{' '}
					{formatCashAmount(CASH_DEPOSIT_LIMIT, 'USD')}.
				</Alert>
			)}
		</>
	);
}

interface BranchCashSummaryProps {
	branch: string;
	title: string;
	detailTo: string;
}

export function BranchCashSummary({ branch, title, detailTo }: BranchCashSummaryProps): JSX.Element {
	const { data, loading, error, cachedAt } = useBranchReceipts({ branch });

	return (
		<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
				<Box display="inline-flex" alignItems="center" gap={0.5}>
					<Typography variant="subtitle1" fontWeight={600}>
						{title}
					</Typography>
					<SectionInfoTooltip content={cashBoxInfoTooltip} />
				</Box>
				<Box display="flex" alignItems="center" gap={1.5}>
					<CachedBadge cachedAt={cachedAt} />
					<Button
						size="small"
						variant="outlined"
						component={RouterLink}
						to={detailTo}
						sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
					>
						Ver detalle
					</Button>
				</Box>
			</Box>

			{loading ? (
				<Skeleton variant="rectangular" height={72} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : (
				<CashTotalsOverview
					totalReceipts={data?.pagination.total ?? 0}
					totalAmount={data?.totals.amountInMainCurrency ?? 0}
					byCurrency={data?.totals.byTransactionCurrency ?? []}
				/>
			)}
		</Paper>
	);
}
